/**
 * Live DOM registry for the animation layer.
 *
 * Seat pills are absolutely positioned from `--seat-x` / `--seat-y`, both of
 * which are recomputed in JS on every render and re-flowed the moment a player
 * dies — so a seat's screen position can never be derived, only measured.
 * `getBoundingClientRect()` also reflects framer-motion's in-flight `layout`
 * transform, which is exactly where a coin should be aimed.
 *
 * Anchors are read at FIRE time and never cached: by the time the next effect
 * runs the table may have re-flowed underneath it.
 */

const anchors = new Map<string, HTMLElement>();
const refCallbacks = new Map<string, (element: HTMLElement | null) => void>();

export function setAnchor(id: string, element: HTMLElement | null) {
  if (element) anchors.set(id, element);
  else anchors.delete(id);
}

/**
 * A stable ref callback per id. Stability matters: an inline arrow would be a
 * new function every render, so React would detach (null) and re-attach the
 * anchor on every snapshot — including the one an effect is mid-flight on.
 */
export function anchorRef(id: string) {
  let callback = refCallbacks.get(id);
  if (!callback) {
    callback = (element: HTMLElement | null) => setAnchor(id, element);
    refCallbacks.set(id, callback);
  }
  return callback;
}

export interface FxPoint {
  x: number;
  y: number;
}

/** Centre of an anchor, expressed in the coordinate space of `origin`. */
export function anchorPoint(id: string, origin: DOMRect | null): FxPoint | null {
  const element = anchors.get(id);
  if (!element || !origin || !element.isConnected) return null;
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return {
    x: rect.left + rect.width / 2 - origin.left,
    y: rect.top + rect.height / 2 - origin.top
  };
}

/** A rival's avatar circle. You have no seat pill, so never use this for yourself. */
export function seatAnchor(playerId: string) {
  return `seat:${playerId}`;
}

/** Your own coin badge, which stands in for your seat. */
export const ME_COINS = "me:coins";
/** Your own fanned hand — where a card you claim appears to come from. */
export const ME_HAND = "me:hand";
/** The middle of the table, i.e. the bank. */
export const TABLE_CENTER = "table:center";
